import { debugConsoleError } from '@/modules/chat-matrix-fast/utils/chatDebugLog'

export enum ChatErrorCode {
  SEND_MESSAGE_FAILED = 'SEND_MESSAGE_FAILED',
  FETCH_CHAT_FAILED = 'FETCH_CHAT_FAILED',
  FETCH_CHATS_FAILED = 'FETCH_CHATS_FAILED',
  FETCH_MESSAGES_FAILED = 'FETCH_MESSAGES_FAILED',
  UPDATE_MESSAGE_FAILED = 'UPDATE_MESSAGE_FAILED',
  DELETE_MESSAGE_FAILED = 'DELETE_MESSAGE_FAILED',
  NETWORK_ERROR = 'NETWORK_ERROR',
  INVALID_INPUT = 'INVALID_INPUT',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  SERVER_ERROR = 'SERVER_ERROR',
  CHAT_NOT_FOUND = 'CHAT_NOT_FOUND',
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
}

export class ChatError extends Error {
  constructor(
    message: string,
    public code: ChatErrorCode,
    public originalError?: unknown,
  ) {
    super(message)
    this.name = 'ChatError'
  }
}

export interface ErrorInfo {
  type: 'chat' | 'network' | 'validation' | 'unknown'
  code?: string | number
  message: string
  originalError?: unknown
}

class ErrorLogger {
  static log(error: Error, context: string): void {
    if (import.meta.env.PROD) {
      debugConsoleError(`[Chat] ${context}:`, error.message)

      return
    }

    console.group(`🔴 [Chat Error] ${context}`)
    debugConsoleError('Error:', error)
    if (error.stack) debugConsoleError('Stack:', error.stack)
    console.groupEnd()
  }
}

export class ErrorHandler {
  static handle(error: unknown, context: string): ErrorInfo {
    if (error instanceof Error) ErrorLogger.log(error, context)
    else if (error instanceof ChatError) ErrorLogger.log(error, context)
    else ErrorLogger.log(new Error(String(error)), context)

    if (error instanceof ChatError) {
      return {
        type: 'chat',
        code: error.code,
        message: this.getLocalizedMessage(error.code),
        originalError: error,
      }
    }

    if (this.isNetworkError(error)) {
      return {
        type: 'network',
        code: this.getNetworkErrorCode(error),
        message: this.getNetworkErrorMessage(error),
        originalError: error,
      }
    }

    if (this.isValidationError(error)) {
      return {
        type: 'validation',
        message: this.getValidationErrorMessage(error),
        originalError: error,
      }
    }

    return {
      type: 'unknown',
      message: 'Произошла неизвестная ошибка. Пожалуйста, попробуйте еще раз.',
      originalError: error,
    }
  }

  private static getLocalizedMessage(code: ChatErrorCode): string {
    const messages: Partial<Record<ChatErrorCode, string>> = {
      [ChatErrorCode.SEND_MESSAGE_FAILED]: 'Не удалось отправить сообщение',
      [ChatErrorCode.FETCH_CHAT_FAILED]: 'Не удалось загрузить чат',
      [ChatErrorCode.NETWORK_ERROR]:
        'Ошибка сети. Проверьте подключение к интернету',
      [ChatErrorCode.INVALID_INPUT]: 'Некорректные данные',
      [ChatErrorCode.FETCH_CHATS_FAILED]: 'Не удалось загрузить чаты',
      [ChatErrorCode.FETCH_MESSAGES_FAILED]: 'Не удалось загрузить сообщения',
      [ChatErrorCode.UPDATE_MESSAGE_FAILED]: 'Не удалось обновить сообщение',
      [ChatErrorCode.DELETE_MESSAGE_FAILED]: 'Не удалось удалить сообщение',
      [ChatErrorCode.UNAUTHORIZED]: 'Необходима авторизация',
      [ChatErrorCode.FORBIDDEN]: 'Недостаточно прав для выполнения операции',
      [ChatErrorCode.NOT_FOUND]: 'Ресурс не найден',
      [ChatErrorCode.SERVER_ERROR]: 'Ошибка сервера. Попробуйте позже',
      [ChatErrorCode.CHAT_NOT_FOUND]: 'Чат не найден',
      [ChatErrorCode.RATE_LIMIT_EXCEEDED]:
        'Слишком много запросов. Пожалуйста, подождите немного',
    }

    return messages[code] || 'Произошла ошибка'
  }

  private static isNetworkError(error: unknown): boolean {
    if (error && typeof error === 'object') {
      interface NetworkError {
        name?: string
        message?: string
        code?: string
        response?: unknown
        request?: unknown
      }
      const err = error as NetworkError

      return (
        err.name === 'NetworkError'
        || err.message?.includes('network')
        || err.message?.includes('Network')
        || err.code === 'NETWORK_ERROR'
        || (err.response === undefined && err.request !== undefined)
      )
    }

    return false
  }

  private static getNetworkErrorCode(error: unknown): number | string {
    if (error && typeof error === 'object') {
      interface NetworkError {
        response?: { status: number }
        code?: string
      }
      const err = error as NetworkError

      return err.response?.status || err.code || 'NETWORK_ERROR'
    }

    return 'NETWORK_ERROR'
  }

  private static getNetworkErrorMessage(error: unknown): string {
    if (error && typeof error === 'object') {
      interface NetworkError {
        response?: { status: number }
      }
      const err = error as NetworkError
      const status = err.response?.status

      if (status === 404) return 'Ресурс не найден'
      if (status === 403) return 'Доступ запрещен'
      if (status === 401) return 'Необходима авторизация'
      if (status !== undefined && status >= 500) {
        return 'Ошибка сервера. Попробуйте позже'
      }
      if (status !== undefined && status >= 400) return 'Ошибка запроса'
    }

    return 'Ошибка сети. Проверьте подключение к интернету'
  }

  private static isValidationError(error: unknown): boolean {
    if (error && typeof error === 'object') {
      interface ValidationError {
        code?: string
        message?: string
      }
      const err = error as ValidationError

      return !!(
        err.code === ChatErrorCode.INVALID_INPUT
        || err.message?.includes('validation')
        || err.message?.includes('invalid')
        || err.message?.includes('валидация')
      )
    }

    return false
  }

  private static getValidationErrorMessage(error: unknown): string {
    if (error && typeof error === 'object') {
      interface ValidationError {
        message?: string
      }
      const err = error as ValidationError
      if (err.message) return err.message.replace(/^validation:\s*/i, '')
    }

    return 'Некорректные данные'
  }
}

export function handleChatError(error: unknown, operation: string): never {
  if (error instanceof Error) ErrorLogger.log(error, operation)

  if (error instanceof ChatError) throw error

  if (error instanceof Error) {
    throw new ChatError(error.message, ChatErrorCode.NETWORK_ERROR, error)
  }

  throw new ChatError('Неизвестная ошибка', ChatErrorCode.NETWORK_ERROR, error)
}
